import { type, type Type } from 'arktype';
import { MS_PER_DAY, dayKeyToUtcMs, isDayRangeWithin } from '../lib/day-key-math';
import { isIsoWeek } from '../lib/iso-week';
import type { BalanceOverviewEntry } from './balance-accounts.model';
import { dayKeySchema } from './day-key.model';
import {
  acceptSuggestionSchema,
  confirmBookingSchema,
  convertMyWeekEntrySchema,
  createMyWeekAbsenceBaseSchema,
  createMyWeekTimeSchema,
  updateMyWeekTimeSchema,
} from './my-week.model';
import type { MyWeekAbsenceTypeOption, MyWeekDay, MyWeekDayStats, MyWeekOpenStamp, MyWeekResponse } from './my-week.model';
import { journalMonthSchema } from './person-journal.model';
import type { ReviewQueueSeverity, VerdictParams } from './review-queue.model';
import type { DryRunObligationDelta } from './rule-catalog.model';
import { narrowError } from './schema-narrow';
import { updateTimetrackingAbsenceBaseSchema } from './timetracking-absence.model';

const OVERVIEW_RANGE_DAYS = 31;
const OVERVIEW_QUERY_ID_LIMIT = 100;
const OVERVIEW_FILTER_ID_LIMIT = 200;
const OVERVIEW_PAIR_LIMIT = 5000;
const OVERVIEW_BOOKING_TARGET_LIMIT = 20000;

function overviewRangeIsValid(dateFrom: string, dateTo: string): boolean {
  return isDayRangeWithin(dateFrom, dateTo, OVERVIEW_RANGE_DAYS);
}

export function isDistinct(values: readonly string[]): boolean {
  return new Set(values).size === values.length;
}

function frozenBookingTargetCount(target: FrozenCommand): number {
  return target.kind === 'day' ? target.bookingIds.length : 1;
}

export const overviewIdSchema = type('1 <= string <= 128');

export function distinctIdListSchema(limit: number) {
  return overviewIdSchema
    .array()
    .atMostLength(limit)
    .narrow((ids, ctx) => isDistinct(ids) || ctx.mustBe('an array of distinct IDs'));
}

const overviewScopeFields = {
  dateFrom: dayKeySchema,
  dateTo: dayKeySchema,
  'teamIds?': distinctIdListSchema(OVERVIEW_QUERY_ID_LIMIT),
  'locationIds?': distinctIdListSchema(OVERVIEW_QUERY_ID_LIMIT),
  'personIds?': distinctIdListSchema(OVERVIEW_QUERY_ID_LIMIT),
};

const overviewScopeSchema = type(overviewScopeFields);

function overviewScopeError(scope: typeof overviewScopeSchema.t): string | null {
  if (!overviewRangeIsValid(scope.dateFrom, scope.dateTo)) return 'a date range of at most 31 ordered days';
  const totalIds = (scope.teamIds?.length ?? 0) + (scope.locationIds?.length ?? 0) + (scope.personIds?.length ?? 0);
  return totalIds <= OVERVIEW_QUERY_ID_LIMIT ? null : 'at most 100 selected IDs in total';
}

export const overviewQuerySchema = type({
  ...overviewScopeFields,
  'balanceZone?': "'red' | 'amber' | 'green' | ('red' | 'amber' | 'green')[]",
  'detail?': "'days' | 'persons' | 'summary'",
  'confirmable?': 'boolean',
  take: '1 <= number.integer <= 20',
  skip: 'number.integer >= 0',
})
  .narrow((query, ctx) => {
    const scopeError = overviewScopeError(query);
    if (scopeError) return ctx.mustBe(scopeError);
    if (Array.isArray(query.balanceZone) && (query.balanceZone.length === 0 || !isDistinct(query.balanceZone))) {
      return ctx.mustBe('one or more distinct balance zones');
    }
    return true;
  })
  .onUndeclaredKey('reject');

export type OverviewQueryInput = typeof overviewQuerySchema.t;

export const overviewBalancesQuerySchema = overviewScopeSchema.narrow(narrowError(overviewScopeError)).onUndeclaredKey('reject');

export type OverviewBalancesQueryInput = typeof overviewBalancesQuerySchema.t;

export type OverviewBalanceZone = 'red' | 'amber' | 'green';

export function overviewBalanceZonesOf(zone: OverviewQueryInput['balanceZone']): readonly OverviewBalanceZone[] {
  if (zone === undefined) return [];
  return typeof zone === 'string' ? [zone] : zone;
}

export const overviewOptionsKindSchema = type("'teams' | 'locations' | 'persons'");

export type OverviewOptionsKind = typeof overviewOptionsKindSchema.t;

// Each kind consumes the other two dimensions; same-dimension IDs are ignored if sent.
export const overviewOptionsQuerySchema = type({
  'q?': 'string <= 200',
  'sort?': "'name:asc' | 'name:desc'",
  'teamIds?': distinctIdListSchema(OVERVIEW_QUERY_ID_LIMIT),
  'locationIds?': distinctIdListSchema(OVERVIEW_QUERY_ID_LIMIT),
  'personIds?': distinctIdListSchema(OVERVIEW_QUERY_ID_LIMIT),
  take: '1 <= number.integer <= 20',
  skip: 'number.integer >= 0',
})
  .narrow((query, ctx) => {
    const totalIds = (query.teamIds?.length ?? 0) + (query.locationIds?.length ?? 0) + (query.personIds?.length ?? 0);
    return totalIds <= OVERVIEW_QUERY_ID_LIMIT || ctx.mustBe('at most 100 selected IDs in total');
  })
  .onUndeclaredKey('reject');

export type OverviewOptionsQueryInput = typeof overviewOptionsQuerySchema.t;

const overviewPreviewFilterSchema = type({
  selection: "'filter'",
  dateFrom: dayKeySchema,
  dateTo: dayKeySchema,
  'teamIds?': distinctIdListSchema(OVERVIEW_FILTER_ID_LIMIT),
  'locationIds?': distinctIdListSchema(OVERVIEW_FILTER_ID_LIMIT),
  'personIds?': distinctIdListSchema(OVERVIEW_FILTER_ID_LIMIT),
})
  .narrow((value, ctx) => overviewRangeIsValid(value.dateFrom, value.dateTo) || ctx.mustBe('a date range of at most 31 ordered days'))
  .onUndeclaredKey('reject');

const overviewPreviewDayTargetSchema = type({
  personId: overviewIdSchema,
  dates: dayKeySchema
    .array()
    .atLeastLength(1)
    .narrow((dates, ctx) => isDistinct(dates) || ctx.mustBe('an array of distinct dates')),
}).onUndeclaredKey('reject');

const overviewPreviewDaysSchema = type({
  selection: "'days'",
  dateFrom: dayKeySchema,
  dateTo: dayKeySchema,
  targets: overviewPreviewDayTargetSchema.array().atLeastLength(1),
})
  .narrow((value, ctx) => {
    if (!overviewRangeIsValid(value.dateFrom, value.dateTo)) return ctx.mustBe('a date range of at most 31 ordered days');
    if (!isDistinct(value.targets.map(target => target.personId))) return ctx.mustBe('targets with distinct personIds');
    const inRange = value.targets.every(target => target.dates.every(date => date >= value.dateFrom && date <= value.dateTo));
    return inRange || ctx.mustBe('target dates inside the stated range');
  })
  .onUndeclaredKey('reject');

const overviewPreviewAdoptPlanSchema = type({
  selection: "'adopt_plan'",
  dateFrom: dayKeySchema,
  dateTo: dayKeySchema,
  personId: overviewIdSchema,
  date: dayKeySchema,
  bookingId: overviewIdSchema,
})
  .narrow((value, ctx) => {
    if (!overviewRangeIsValid(value.dateFrom, value.dateTo)) return ctx.mustBe('a date range of at most 31 ordered days');
    return (value.date >= value.dateFrom && value.date <= value.dateTo) || ctx.mustBe('a target date inside the stated range');
  })
  .onUndeclaredKey('reject');

const overviewPreviewAcceptSuggestionSchema = type({
  selection: "'accept_suggestion'",
  dateFrom: dayKeySchema,
  dateTo: dayKeySchema,
  personId: overviewIdSchema,
  date: dayKeySchema,
  sourceBookingId: overviewIdSchema,
  sourceDate: dayKeySchema,
})
  .narrow((value, ctx) => {
    if (!overviewRangeIsValid(value.dateFrom, value.dateTo)) return ctx.mustBe('a date range of at most 31 ordered days');
    return (value.date >= value.dateFrom && value.date <= value.dateTo) || ctx.mustBe('a target date inside the stated range');
  })
  .onUndeclaredKey('reject');

export const overviewPreviewRequestSchema = overviewPreviewFilterSchema
  .or(overviewPreviewDaysSchema)
  .or(overviewPreviewAdoptPlanSchema)
  .or(overviewPreviewAcceptSuggestionSchema);

export type OverviewPreviewRequestInput = typeof overviewPreviewRequestSchema.t;

export const overviewRevisionsSchema = type({
  workRevision: '1 <= string <= 128',
  configRevision: '1 <= string <= 128',
}).onUndeclaredKey('reject');

export type Revisions = typeof overviewRevisionsSchema.t;

const frozenDayCommandSchema = type({
  kind: "'day'",
  bookingIds: overviewIdSchema.array().narrow((ids, ctx) => isDistinct(ids) || ctx.mustBe('an array of distinct booking IDs')),
}).onUndeclaredKey('reject');

const frozenAdoptPlanCommandSchema = type({
  kind: "'adopt_plan'",
  bookingId: overviewIdSchema,
}).onUndeclaredKey('reject');

const frozenAcceptSuggestionCommandSchema = type({
  kind: "'accept_suggestion'",
  sourceBookingId: overviewIdSchema,
  sourceDate: dayKeySchema,
}).onUndeclaredKey('reject');

export const frozenCommandSchema = frozenDayCommandSchema.or(frozenAdoptPlanCommandSchema).or(frozenAcceptSuggestionCommandSchema);

export type FrozenCommand = typeof frozenCommandSchema.t;

const frozenDayTargetSchema = type({
  date: dayKeySchema,
  target: frozenCommandSchema,
}).onUndeclaredKey('reject');

export const frozenPersonSchema = type({
  personId: overviewIdSchema,
  expected: overviewRevisionsSchema,
  days: frozenDayTargetSchema.array().atLeastLength(1),
})
  .narrow((person, ctx) => isDistinct(person.days.map(day => day.date)) || ctx.mustBe('days with distinct dates'))
  .onUndeclaredKey('reject');

export type FrozenPerson = typeof frozenPersonSchema.t;

export const overviewConfirmRequestSchema = type({
  serverTodayKey: dayKeySchema,
  targets: frozenPersonSchema.array().atLeastLength(1),
  force: 'boolean',
})
  .narrow((request, ctx) => {
    if (!isDistinct(request.targets.map(target => target.personId))) return ctx.mustBe('targets with distinct personIds');
    const dates = request.targets.flatMap(target => target.days.map(day => day.date));
    let minDate = '';
    let maxDate = '';
    for (const date of dates) {
      if (minDate === '' || date < minDate) minDate = date;
      if (maxDate === '' || date > maxDate) maxDate = date;
    }
    if (dayKeyToUtcMs(maxDate) - dayKeyToUtcMs(minDate) > (OVERVIEW_RANGE_DAYS - 1) * MS_PER_DAY)
      return ctx.mustBe('targets within a single 31-day span');
    if (dates.length > OVERVIEW_PAIR_LIMIT) return ctx.mustBe('at most 5000 person/day pairs');
    const bookingTargets = request.targets.flatMap(target => target.days.map(day => frozenBookingTargetCount(day.target)));
    const bookingTotal = bookingTargets.reduce((sum, count) => sum + count, 0);
    return bookingTotal <= OVERVIEW_BOOKING_TARGET_LIMIT || ctx.mustBe('at most 20000 booking targets');
  })
  .onUndeclaredKey('reject');

export type OverviewConfirmRequestInput = typeof overviewConfirmRequestSchema.t;

// Self-service twin of overviewConfirmRequestSchema: no personId (session-derived) and no force (never self-grantable).
export const myWeekLockCommitBodySchema = type({
  serverTodayKey: dayKeySchema,
  expected: overviewRevisionsSchema,
  days: frozenDayTargetSchema.array().atLeastLength(1),
})
  .narrow((body, ctx) => {
    const dates = body.days.map(day => day.date);
    if (!isDistinct(dates)) return ctx.mustBe('days with distinct dates');
    const sorted = [...dates].sort((left, right) => left.localeCompare(right));
    if (dayKeyToUtcMs(sorted[sorted.length - 1]) - dayKeyToUtcMs(sorted[0]) > (OVERVIEW_RANGE_DAYS - 1) * MS_PER_DAY)
      return ctx.mustBe('days within a single 31-day span');
    const bookingTotal = body.days.reduce((sum, day) => sum + frozenBookingTargetCount(day.target), 0);
    return bookingTotal <= OVERVIEW_BOOKING_TARGET_LIMIT || ctx.mustBe('at most 20000 booking targets');
  })
  .onUndeclaredKey('reject');

export type MyWeekLockCommitBodyInput = typeof myWeekLockCommitBodySchema.t;

export interface MyWeekQueryInput {
  week?: string;
  month?: string;
  dateFrom?: string;
  dateTo?: string;
}

function myWeekQueryError(query: MyWeekQueryInput): string | null {
  const hasRange = query.dateFrom !== undefined || query.dateTo !== undefined;
  if (hasRange && (query.dateFrom === undefined || query.dateTo === undefined)) return 'dateFrom and dateTo given together';
  const selectors = [query.week !== undefined, query.month !== undefined, hasRange].filter(Boolean).length;
  if (selectors > 1) return 'at most one of week, month or dateFrom/dateTo';
  const rangeInvalid = query.dateFrom !== undefined && query.dateTo !== undefined && !overviewRangeIsValid(query.dateFrom, query.dateTo);
  return rangeInvalid ? 'a date range of at most 31 ordered days' : null;
}

export const myWeekQuerySchema: Type<MyWeekQueryInput> = type({
  'week?': type('string').narrow((week, ctx) => isIsoWeek(week) || ctx.mustBe('an ISO week (YYYY-Www)')),
  'month?': journalMonthSchema,
  'dateFrom?': dayKeySchema,
  'dateTo?': dayKeySchema,
})
  .narrow(narrowError(myWeekQueryError))
  .onUndeclaredKey('reject');

export type MonthLockBlockingReason = 'month_not_ended' | 'open_stamp' | 'findings_unjustified';

export interface OverviewDayReview {
  pendingReviewCount: number;
  hasPendingDelete: boolean;
  latestCompleteEvaluation: {
    severity: ReviewQueueSeverity | null;
    issues: NonNullable<MyWeekDayStats['issues']>;
  } | null;
  complete: boolean;
}

export interface OverviewPerson {
  id: string;
  firstName: string;
  lastName: string;
  avatarUrl: string | null;
}

export interface OverviewLocationEntry {
  locationId: string;
  name: string;
  icon: string | null;
  color: string | null;
  imageUrl: string | null;
  startTime: string;
  endTime: string;
}

export type OverviewWorkLocationSource = 'override' | 'template' | 'shift-assignment' | 'default-shift' | 'employment-target' | 'none';

export interface OverviewDayWorkLocation {
  source: OverviewWorkLocationSource;
  entries: OverviewLocationEntry[];
}

// forecast: the value projects today up to the range end on top of the ledger; the server always sets it, absent reads as false.
export interface OverviewFlexitimeBalance {
  valueHours: number | null;
  zone: BalanceOverviewEntry['zone'];
  forecast?: boolean;
}

export interface OverviewBalanceSummary {
  asOf: typeof dayKeySchema.t;
  forecast?: boolean;
  populationTotal: number;
  red: number;
  amber: number;
  green: number;
  unclassified: number;
  averageBalance: number | null;
  averageDenominator: number;
  complete: boolean;
}

export interface OverviewPersonGroup {
  person: OverviewPerson;
  days: MyWeekDay[];
  // Day before the requested range, only so overnight time can continue into the first day; not part of days or totals.
  precedingDay?: MyWeekDay | null;
  reviewByDate: Record<string, OverviewDayReview>;
  totals: MyWeekResponse['totals'];
  flexitimeBalance?: OverviewFlexitimeBalance;
  absenceTypes: MyWeekAbsenceTypeOption[];
  openStamp: MyWeekOpenStamp | null;
  revisions: Revisions;
  locationByDate: Record<string, OverviewDayWorkLocation>;
  // Individual calendar carrying the person's work-location override slots; null when none is assigned.
  calendarId: string | null;
}

// confirmable*: eligible_clean/eligible_warning person-days and persons over ALL matched people; omitted without the confirm grant or confirmable=false.
export interface OverviewResponse {
  data: OverviewPersonGroup[];
  meta: { take: number; skip: number; total: number; hasMore: boolean; confirmableDays?: number; confirmablePersons?: number };
  serverTodayKey: string;
  balanceSummary?: OverviewBalanceSummary;
}

// detail=persons: identity and pending-review count of the page only; no day assembly, totals or balances.
export interface OverviewPersonHeader {
  person: OverviewPerson;
  pendingReviewCount: number;
}

export interface OverviewPersonHeadersResponse {
  data: OverviewPersonHeader[];
  meta: OverviewResponse['meta'];
  serverTodayKey: string;
}

// detail=summary: aggregates over ALL matched people of the range, not one page; carries no per-person days.
export interface OverviewSummary {
  totals: { plannedMinutes: number; actualMinutes: number; deviationMinutes: number };
  balanceMinutes: number;
  issueCounts: { warn: number; block: number };
  issuesByPerson: Record<string, { warn: number; block: number }>;
}

export interface OverviewSummaryResponse {
  summary: OverviewSummary;
  meta: { total: number };
  serverTodayKey: string;
}

export interface OverviewBalancesResponse {
  serverTodayKey: string;
  balanceSummary: OverviewBalanceSummary;
  byPerson: Record<string, OverviewFlexitimeBalance>;
}

export interface OverviewOption {
  id: string;
  label: string;
  // Persons carry their avatar here (managed file URL or legacy URL); teams/locations leave it null.
  avatarUrl: string | null;
  // Teams and locations carry their own configured visual; persons leave these null.
  icon: string | null;
  color: string | null;
  imageUrl: string | null;
}

export interface OptionsResponse {
  data: OverviewOption[];
  meta: { take: number; skip: number; total: number; hasMore: boolean; universeTotal: number };
  serverTodayKey: string;
}

export const previewEligibilities = ['eligible_clean', 'eligible_warning', 'force_eligible', 'unforceable'] as const;
export type PreviewEligibility = (typeof previewEligibilities)[number];

export interface OverviewPreviewIssue {
  key: string;
  severity: ReviewQueueSeverity;
  overridable: boolean;
  params: VerdictParams | null;
}

// R-9a: alias of the domain obligation-delta contract DryRunObligationDelta.
export type DomainObligationDelta = DryRunObligationDelta;

export interface OverviewPreviewDay {
  date: string;
  target: FrozenCommand;
  eligibility: PreviewEligibility;
  suggestionCandidate?: { startTime: string; endTime: string };
  skipReasons: string[];
  issues: OverviewPreviewIssue[];
  obligationDeltas: DomainObligationDelta[];
}

export interface OverviewPreviewPerson {
  personId: string;
  personName: string;
  expected: Revisions;
  days: OverviewPreviewDay[];
}

export interface OverviewPreview {
  serverTodayKey: string;
  complete: boolean;
  incompleteReasons: string[];
  counts: {
    matchedPersons: number;
    matchedDays: number;
    candidateBookings: number;
    eligibleCleanDays: number;
    eligibleWarningDays: number;
    forceEligibleDays: number;
    unforceableDays: number;
  };
  persons: OverviewPreviewPerson[];
}

export type OverviewConfirmPersonOutcome = 'committed' | 'unchanged' | 'skipped' | 'conflict' | 'failed';
export type OverviewConfirmDayOutcome = 'changed' | 'unchanged' | 'skipped' | 'conflict' | 'failed';

export interface OverviewConfirmDayResult {
  date: string;
  outcome: OverviewConfirmDayOutcome;
  reasons: string[];
}

export interface OverviewConfirmPersonResult {
  personId: string;
  outcome: OverviewConfirmPersonOutcome;
  code?: string;
  days: OverviewConfirmDayResult[];
}

export interface OverviewConfirmResult {
  changedDayCount: number;
  unchangedDayCount: number;
  skippedDayCount: number;
  conflictedDayCount: number;
  failedDayCount: number;
  persons: OverviewConfirmPersonResult[];
}

export const overviewUnlockDayBodySchema = type({ 'expected?': overviewRevisionsSchema }).onUndeclaredKey('reject');

export type OverviewUnlockDayBodyInput = typeof overviewUnlockDayBodySchema.t;

export const overviewDraftCorrectionBodySchema = confirmBookingSchema
  .and(type({ expected: overviewRevisionsSchema }))
  .onUndeclaredKey('reject');

export type OverviewDraftCorrectionBodyInput = typeof overviewDraftCorrectionBodySchema.t;

export const overviewCreateTimeBodySchema = createMyWeekTimeSchema
  .and(type({ expected: overviewRevisionsSchema }))
  .onUndeclaredKey('reject');

export type OverviewCreateTimeBodyInput = typeof overviewCreateTimeBodySchema.t;

export const overviewUpdateTimeBodySchema = updateMyWeekTimeSchema
  .and(type({ expected: overviewRevisionsSchema }))
  .onUndeclaredKey('reject');

export type OverviewUpdateTimeBodyInput = typeof overviewUpdateTimeBodySchema.t;

export const overviewVoidTimeBodySchema = type({ expected: overviewRevisionsSchema }).onUndeclaredKey('reject');

export type OverviewVoidTimeBodyInput = typeof overviewVoidTimeBodySchema.t;

export const overviewAbortDeleteBodySchema = type({ expected: overviewRevisionsSchema }).onUndeclaredKey('reject');

export type OverviewAbortDeleteBodyInput = typeof overviewAbortDeleteBodySchema.t;

export const overviewDiscardStampBodySchema = type({ expected: overviewRevisionsSchema }).onUndeclaredKey('reject');

export type OverviewDiscardStampBodyInput = typeof overviewDiscardStampBodySchema.t;

export const overviewCapturePlanBodySchema = type({ expected: overviewRevisionsSchema }).onUndeclaredKey('reject');

export type OverviewCapturePlanBodyInput = typeof overviewCapturePlanBodySchema.t;

export const overviewDismissSuggestionBodySchema = acceptSuggestionSchema
  .and(type({ expected: overviewRevisionsSchema }))
  .onUndeclaredKey('reject');

export type OverviewDismissSuggestionBodyInput = typeof overviewDismissSuggestionBodySchema.t;

export const overviewCreateAbsenceBodySchema = createMyWeekAbsenceBaseSchema
  .and(type({ expected: overviewRevisionsSchema }))
  .onUndeclaredKey('reject');

export type OverviewCreateAbsenceBodyInput = typeof overviewCreateAbsenceBodySchema.t;

export const overviewRemoveAbsenceBodySchema = type({ expected: overviewRevisionsSchema }).onUndeclaredKey('reject');

export type OverviewRemoveAbsenceBodyInput = typeof overviewRemoveAbsenceBodySchema.t;

export const overviewChangeAbsenceBodySchema = updateTimetrackingAbsenceBaseSchema
  .and(type({ expected: overviewRevisionsSchema }))
  .onUndeclaredKey('reject');

export type OverviewChangeAbsenceBodyInput = typeof overviewChangeAbsenceBodySchema.t;

export const overviewConvertBodySchema = convertMyWeekEntrySchema
  .and(type({ expected: overviewRevisionsSchema }))
  .onUndeclaredKey('reject');

export type OverviewConvertBodyInput = typeof overviewConvertBodySchema.t;

export const overviewExcludePlanBodySchema = type({ expected: overviewRevisionsSchema }).onUndeclaredKey('reject');

export type OverviewExcludePlanBodyInput = typeof overviewExcludePlanBodySchema.t;

export const overviewIncludePlanBodySchema = type({ expected: overviewRevisionsSchema }).onUndeclaredKey('reject');

export type OverviewIncludePlanBodyInput = typeof overviewIncludePlanBodySchema.t;
